import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Catches render/lifecycle errors in its subtree and shows a friendly fallback
 * instead of a blank page. (React error boundaries must be class components.)
 * Keep one near the top of the app as a safety net, and one around the main
 * view area (keyed by the active view) so navigating away recovers.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info?.componentStack);
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[40vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-center">
          <div className="mx-auto mb-3 h-11 w-11 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Something went wrong</h2>
          <p className="text-sm text-slate-600 mt-1">
            This part of the app hit an unexpected error. You can try again, or reload the page.
          </p>
          {this.state.error?.message && (
            <pre className="mt-3 text-left text-xs bg-slate-50 border border-slate-200 rounded-md p-2 overflow-auto max-h-32 text-slate-500 whitespace-pre-wrap">
              {String(this.state.error.message)}
            </pre>
          )}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" onClick={this.handleReset}>
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
